import type { CSSProperties } from "react"

const COOGO_MARK = "/images/brand/coogo_ff_mark.svg"
const COOGO_LOCKUP = "/images/brand/coogo_lockup.svg"
const COOGO_LOCKUP_ASPECT_RATIO = 312 / 120

/**
 * The wordmark's alternative text — the brand, which is the same word in
 * every language and must NOT be translated.
 *
 * Exported because the accessibility-surface gate derives its keep-Latin
 * allowlist from here rather than retyping the word: if the wordmark's alt
 * text ever changes, the allowlist follows it instead of quietly covering a
 * word that is no longer on the surface.
 */
export const COOGO_WORDMARK_ALT = "coogo"

export interface CogoLogoProps {
  size?: number
  variant?: "default" | "speed" | "white" | "charcoal" | "living"
  animated?: boolean
  className?: string
  style?: CSSProperties
}

/**
 * Official Coogo fast-forward mark.
 *
 * The asset is supplied by the Coogo design-system handoff. Keep the legacy
 * component name so product code and integrations do not need a risky rename.
 */
export function CogoLogoBrand({
  size = 28,
  className,
  style,
}: CogoLogoProps) {
  return (
    <img
      src={COOGO_MARK}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      decoding="async"
      draggable={false}
      className={className}
      data-coogo-logo="mark"
      style={{
        display: "inline-block",
        flexShrink: 0,
        width: size,
        height: size,
        objectFit: "contain",
        verticalAlign: "middle",
        ...style,
      }}
    />
  )
}

export const CogoLogoConcept1 = CogoLogoBrand
export const CogoLogoConcept2 = CogoLogoBrand
export const CogoLogoConcept3 = CogoLogoBrand
export const CogoLogoConcept4 = CogoLogoBrand

export type CogoWordmarkProps = CogoLogoProps

/** Official Coogo horizontal lockup supplied by the brand system. */
export function CogoWordmark({
  size = 28,
  className,
  style,
}: CogoWordmarkProps) {
  const width = Number((size * COOGO_LOCKUP_ASPECT_RATIO).toFixed(2))
  return (
    <img
      src={COOGO_LOCKUP}
      alt={COOGO_WORDMARK_ALT}
      width={width}
      height={size}
      decoding="async"
      draggable={false}
      className={className}
      data-coogo-logo="lockup"
      style={{
        display: "inline-block",
        width,
        height: size,
        objectFit: "contain",
        objectPosition: "left center",
        verticalAlign: "middle",
        ...style,
      }}
    />
  )
}
